import {hasProcessAccess} from "@buildwithflux/shared";
import {z} from "zod";

export const AutopilotClientConfig = z.object({
    host: z.string().startsWith("http"),
});
export type AutopilotClientConfig = z.infer<typeof AutopilotClientConfig>;

export const AutopilotClientProcessEnv = z.object({
    REACT_APP_AUTOPILOT_HOST: z.string(),
});
export type AutopilotClientProcessEnv = z.infer<typeof AutopilotClientProcessEnv>;

/**
 * Safely obtain process.env with DefinePlugin support
 *
 * These variable are actually filled in by the DefinePlugin at bundle time, so they are compile-time values,
 * baked into the build. For this reason, we can't inject the whole of `process.env` as an application service: the
 * define plugin looks specifically for these full property accesses.
 *
 * This makes the accesses of the environment in getConfigFromEnv() work for both bundled apps and Node
 *
 * This function can't be abstracted: the environment variables accesses must appear in the code as the literal
 * "process.env.REACT_APP_ENVIRONMENT_NAME" name for them to be replaced by the bundler
 */
function getProcessEnvSafely(): NodeJS.ProcessEnv {
    return {
        ...((hasProcessAccess() ? process?.env ?? {} : {}) as NodeJS.ProcessEnv),
        REACT_APP_AUTOPILOT_HOST: process.env.REACT_APP_AUTOPILOT_HOST,
    };
}

export function loadAutopilotClientConfigFromEnv(
    overrideProcessEnv: Partial<typeof process.env> = {},
): AutopilotClientConfig {
    const config = AutopilotClientProcessEnv.parse({...getProcessEnvSafely(), ...overrideProcessEnv});

    return AutopilotClientConfig.parse({
        host: config.REACT_APP_AUTOPILOT_HOST,
    });
}
